<template>
  <div class="driver-details-container">
    <!-- Loading Component -->
    <CustomLoader v-if="isLoading" :width="70" />

    <b-card class="mainCardBG">
      <b-card-text>
        <!-- Header Navigation -->
        <b-navbar class="formCard" style="border-radius: 5px !important; padding-left: 0px; height: auto !important;"
          toggleable="lg" type="dark" variant="primary">
          <b-navbar-brand center>Driver Details</b-navbar-brand>

          <b-navbar-nav class="ml-auto">
            <b-nav-form>
              <b-button size="sm" variant="warning" @click="openDriverModal" :disabled="isLoading" class="mr-2">
                <md-icon>add</md-icon>New Driver
              </b-button>

              <b-button :disabled="isImporting" @click="importExcel" size="sm" variant="outline-success" class="mr-2">
                <md-icon>file_upload</md-icon>{{ importButtonText }}
                <b-spinner v-if="isImporting" small label="Importing..."></b-spinner>
              </b-button>

              <b-button :disabled="isExporting" @click="exportDriverReport" size="sm" variant="outline-info">
                <md-icon>file_download</md-icon>{{ exportButtonText }}
                <b-spinner v-if="isExporting" small label="Exporting..."></b-spinner>
              </b-button>
            </b-nav-form>
          </b-navbar-nav>
        </b-navbar>

        <!-- Data Summary -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <h5 class="text-muted mb-0">
            <small>{{ totalRows }} Entries</small>
          </h5>
          <div class="d-flex align-items-center">
            <b-form-select v-model="perPage" :options="pageOptions" size="sm" class="mr-2"
              style="width: 80px;"></b-form-select>
            <span class="text-muted">per page</span>
          </div>
        </div>

        <!-- Search Input -->
        <div class="mt-3">
          <b-input-group>
            <b-input-group-prepend>
              <b-input-group-text>
                <md-icon>search</md-icon>
              </b-input-group-text>
            </b-input-group-prepend>
            <b-form-input v-model="searchQuery" placeholder="Search drivers..." @input="handleSearch"
              :disabled="isLoading"></b-form-input>
            <b-input-group-append v-if="searchQuery">
              <b-button variant="outline-secondary" @click="clearSearch" size="sm">
                <md-icon>close</md-icon>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <!-- Main Table -->
        <div class="table-responsive tableMain mt-3">
          <b-table show-empty stacked="md" :items="driverList" :fields="tableFields" :per-page="perPage"
            :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :sort-direction="sortDirection" :busy="isLoading"
            empty-text="No drivers found" empty-filtered-text="No drivers match your search criteria">
            <!-- Driver Name Column -->
            <template slot="driverName" slot-scope="row">
              <b-button variant="link" class="p-0 text-primary" size="sm" @click="viewDriverDetails(row.item.driverId)"
                :disabled="isLoading">
                {{ row.value }}
              </b-button>
            </template>

            <!-- Contact Column -->
            <template slot="driverContact" slot-scope="row">
              <span class="text-muted">{{ row.value || 'N/A' }}</span>
            </template>

            <!-- Vehicle Number Column -->
            <template slot="driverVehicleNumber" slot-scope="row">
              <span class="font-weight-bold text-info">{{ row.value || 'N/A' }}</span>
            </template>

            <!-- Date of Joining Column -->
            <template slot="driverDateOfJoining" slot-scope="row">
              <span class="text-success">{{ formatDate(row.value) }}</span>
            </template>

            <!-- Date of Leaving Column -->
            <template slot="driverDateOfLeaving" slot-scope="row">
              <span class="text-danger">{{ formatDate(row.value) }}</span>
            </template>

            <!-- Date of Birth Column -->
            <template slot="driverDateOfBirth" slot-scope="row">
              <span class="text-info">{{ formatDate(row.value) }}</span>
            </template>

            <!-- DL Number Column -->
            <template slot="license_no" slot-scope="row">
              <span class="font-monospace text-primary">{{ row.value || 'N/A' }}</span>
            </template>

            <!-- UAN Number Column -->
            <template slot="driverUANNumber" slot-scope="row">
              <span class="font-monospace text-secondary">{{ row.value || 'N/A' }}</span>
            </template>

            <!-- Working Year Column -->
            <template slot="driverWorkingYear" slot-scope="row">
              <b-badge variant="info">{{ row.value || 'N/A' }}</b-badge>
            </template>

            <!-- ESIC Column -->
            <template slot="driverEsic" slot-scope="row">
              <span class="font-monospace">{{ row.value || 'N/A' }}</span>
            </template>

            <!-- Account No Column -->
            <template slot="accountNo" slot-scope="row">
              <span class="font-monospace">{{ row.value || 'N/A' }}</span>
            </template>

            <!-- Driver Type Column -->
            <template slot="driverType" slot-scope="row">
              <b-badge :variant="row.value === '1' ? 'success' : 'warning'">
                {{ row.value === '1' ? 'Permanent' : (row.value === '2' ? 'Temporary' : 'N/A') }}
              </b-badge>
            </template>

            <!-- Actions Column -->
            <template slot="actions" slot-scope="row">
              <div class="d-flex">
                <b-button size="sm" variant="outline-info" @click="viewDriverProfile(row.item.driverId)" :disabled="isLoading"
                  title="View Driver Profile" class="mr-1">
                  <md-icon>visibility</md-icon>
                </b-button>
                <b-button size="sm" variant="outline-primary" @click="openDriverModal(row.item)" :disabled="isLoading"
                  title="Edit Driver" class="mr-1">
                  <md-icon>edit</md-icon>
                </b-button>
                <b-button size="sm" variant="outline-danger" @click="confirmDeleteDriver(row.item)"
                  :disabled="isLoading" title="Delete Driver">
                  <md-icon>delete_sweep</md-icon>
                </b-button>
              </div>
            </template>

            <!-- Loading State -->
            <template slot="table-busy">
              <div class="text-center my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>
          </b-table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-center mt-3">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" limit="5"
            @change="handlePageChange" :disabled="isLoading"></b-pagination>
        </div>
      </b-card-text>
    </b-card>

    <!-- Delete Confirmation Modal -->
    <b-modal v-model="showDeleteModal" title="Confirm Delete" @ok="deleteDriver" @cancel="cancelDelete"
      ok-variant="danger" ok-title="Delete" cancel-title="Cancel">
      <p class="my-4">
        Are you sure you want to delete driver <strong>{{ driverToDelete && driverToDelete.driverName }}</strong>?
        This action cannot be undone.
      </p>
    </b-modal>

    <!-- Success Alert -->
    <b-alert v-model="showSuccessToast" variant="success" dismissible @dismissed="showSuccessToast = false">
      {{ successMessage }}
    </b-alert>

    <!-- Add/Edit Driver Modal -->
    <b-modal no-close-on-esc no-close-on-backdrop size="xl" ref="Driver-modal" hide-footer
      :title="isEditMode ? 'Edit Driver' : 'Add Driver'" @hidden="resetDriverModal">
      <b-form @submit.prevent="addDriver">
        <!-- Basic Information -->
        <h6 class="text-primary mb-3">Basic Information</h6>
        <b-row>
          <b-col md="4">
            <label>Location <span class="error">*</span></label>
            <b-form-input size="sm" id="location" name="location" v-model="driverModal.location"
              placeholder="Enter Location" :state="!errorList.location"></b-form-input>
            <span class="error" v-if="errorList.location">{{ errorList.location }}</span>
          </b-col>
          <b-col md="4">
            <label>Vehicle Number</label>
            <b-form-input size="sm" id="vehicleNumber" name="vehicleNumber" v-model="driverModal.vehicleNumber"
              placeholder="Enter Vehicle Number"></b-form-input>
          </b-col>
          <b-col md="4">
            <label>Driver Type <span class="error">*</span></label>
            <b-form-select size="sm" id="driverType" name="driverType" v-model="driverModal.driverType"
              :options="driverTypeOptions" :state="!errorList.driverType"></b-form-select>
            <span class="error" v-if="errorList.driverType">{{ errorList.driverType }}</span>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col md="4">
            <label>E.Code</label>
            <b-form-input size="sm" id="eCode" name="eCode" v-model="driverModal.eCode"
              placeholder="Enter E.Code"></b-form-input>
          </b-col>
          <b-col md="4">
            <label>Name of Driver <span class="error">*</span></label>
            <b-form-input size="sm" id="driverName" name="driverName" v-model="driverModal.driverName"
              placeholder="Enter Driver Name" :state="!errorList.driverName"></b-form-input>
            <span class="error" v-if="errorList.driverName">{{ errorList.driverName }}</span>
          </b-col>
          <b-col md="4">
            <label>Father's Name <span class="error">*</span></label>
            <b-form-input size="sm" id="fathersName" name="fathersName" v-model="driverModal.fathersName"
              placeholder="Enter Father's Name" :state="!errorList.fathersName"></b-form-input>
            <span class="error" v-if="errorList.fathersName">{{ errorList.fathersName }}</span>
          </b-col>
        </b-row>

        <!-- Contact Information -->
        <h6 class="text-primary mb-3 mt-4">Contact Information</h6>
        <b-row>
          <b-col md="4">
            <label>Mobile Number <span class="error">*</span></label>
            <b-form-input size="sm" id="mobileNumber" name="mobileNumber" v-model="driverModal.mobileNumber"
              placeholder="Enter Mobile Number" :state="!errorList.mobileNumber"
              @blur="validateMobile(driverModal.mobileNumber)"></b-form-input>
            <span class="error" v-if="errorList.mobileNumber">{{ errorList.mobileNumber }}</span>
          </b-col>
          <b-col md="4">
            <label>Date of Joining</label>
            <b-form-input size="sm" type="date" id="dateOfJoining" name="dateOfJoining"
              v-model="driverModal.dateOfJoining"></b-form-input>
          </b-col>
          <b-col md="4">
            <label>Date of Leaving</label>
            <b-form-input size="sm" type="date" id="dateOfLeaving" name="dateOfLeaving"
              v-model="driverModal.dateOfLeaving"></b-form-input>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col md="4">
            <label>Date of Birth</label>
            <b-form-input size="sm" type="date" id="dateOfBirth" name="dateOfBirth"
              v-model="driverModal.dateOfBirth"></b-form-input>
          </b-col>
          <b-col md="4">
            <label>Aadhar Number</label>
            <b-form-input size="sm" id="aadharNo" name="aadharNo" v-model="driverModal.aadharNo"
              placeholder="XXXX-XXXX-XXXX" @blur="validateAadhar(driverModal.aadharNo)"></b-form-input>
            <span class="error" v-if="errorList.aadharNo">{{ errorList.aadharNo }}</span>
          </b-col>
          <b-col md="4">
            <label>UAN Number</label>
            <b-form-input size="sm" id="uanNumber" name="uanNumber" v-model="driverModal.uanNumber"
              placeholder="Enter UAN Number"></b-form-input>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col md="4">
            <label>E.S.I. Number</label>
            <b-form-input size="sm" id="esiNo" name="esiNo" v-model="driverModal.esiNo"
              placeholder="Enter ESI Number"></b-form-input>
          </b-col>
          <b-col md="4">
            <label>DL Number <span class="error">*</span></label>
            <b-form-input size="sm" id="dlNumber" name="dlNumber" v-model="driverModal.dlNumber"
              placeholder="Enter DL Number" :state="!errorList.dlNumber"
              @blur="validateDL(driverModal.dlNumber)"></b-form-input>
            <span class="error" v-if="errorList.dlNumber">{{ errorList.dlNumber }}</span>
          </b-col>
        </b-row>

        <!-- Action Buttons -->
        <b-row class="mt-4">
          <b-col class="text-center">
            <b-button type="submit" variant="primary" :disabled="isSubmitting" size="sm" class="mr-2">
              <b-spinner small v-if="isSubmitting"></b-spinner>
              {{ isSubmitting ? 'Saving...' : (isEditMode ? 'Update Driver' : 'Save Driver') }}
            </b-button>
            <b-button variant="outline-secondary" @click="hideDriverModal" size="sm">
              Cancel
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>


    <!-- Error Alert -->
    <b-alert v-model="showErrorToast" variant="danger" dismissible @dismissed="showErrorToast = false">
      {{ errorMessage }}
    </b-alert>
  </div>
</template>

<script>
import CustomLoader from "../../common/customLoader.vue";
import { URL_BASE } from "../../../helper/constant.js";
import { globalPermissionStatus } from "../../../helper/collection";
import { read, utils } from "xlsx";
import DriverService from "../../../services/DriverService.js";

export default {
  name: "DriverDetailsView",

  components: {
    CustomLoader,
  },

  data() {
    return {
      // Driver Modal Data
      driverModal: {
        driverId: "",
        location: "",
        vehicleNumber: "",
        driverType: "",
        eCode: "",
        driverName: "",
        fathersName: "",
        mobileNumber: "",
        dateOfJoining: "",
        dateOfLeaving: "",
        dateOfBirth: "",
        aadharNo: "",
        uanNumber: "",
        esiNo: "",
        dlNumber: "",
      },
      driverTypeOptions: [
        { value: "Permanent", text: "Permanent" },
        { value: "Temporary", text: "Temporary" },
      ],
      errorList: {
        location: "",
        driverType: "",
        driverName: "",
        fathersName: "",
        mobileNumber: "",
        aadharNo: "",
        dlNumber: "",
      },
      isSubmitting: false,
      isEditMode: false,

      // Loading states
      isLoading: false,
      isExporting: false,
      isImporting: false,

      // Table data
      driverList: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],

      // Sorting and filtering
      sortBy: null,
      sortDesc: false,
      sortDirection: "asc",
      searchQuery: "",
      storedCurrentPage: 0,

            // Table fields configuration
      tableFields: [
        {
          key: "driverName",
          label: "Driver Name",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverContact",
          label: "Contact No.",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverVehicleNumber",
          label: "Vehicle Number",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverDateOfJoining",
          label: "Date of Joining",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverDateOfLeaving",
          label: "Date of Leaving",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverDateOfBirth",
          label: "Date of Birth",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "license_no",
          label: "DL Number",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverUANNumber",
          label: "UAN Number",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverWorkingYear",
          label: "Working Year",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverEsic",
          label: "ESIC",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "accountNo",
          label: "Account No.",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "driverType",
          label: "Driver Type",
          sortable: true,
          sortDirection: "asc",
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
        },
      ],

      // Modal states
      showDeleteModal: false,
      driverToDelete: null,

      // Toast states
      showSuccessToast: false,
      showErrorToast: false,
      successMessage: "",
      errorMessage: "",
    };
  },

  computed: {
    hasDriverData() {
      return this.driverList.length > 0;
    },

    exportButtonText() {
      return this.isExporting ? "Exporting..." : "Export Excel";
    },

    importButtonText() {
      return this.isImporting ? "Importing..." : "Import Excel";
    },



    sortOptions() {
      return this.tableFields
        .filter(field => field.sortable)
        .map(field => ({
          text: field.label,
          value: field.key
        }));
    },
  },

  beforeMount() {
    this.initializeComponent();
  },

  beforeDestroy() {
    // Cleanup if needed
  },

  methods: {
    // Validation Methods
    validateMobile(mobileNumber) {
      if (!mobileNumber) {
        this.errorList.mobileNumber = "Please enter mobile number";
        return false;
      }
      const pattern = /^[6-9]\d{9}$/;
      if (!pattern.test(mobileNumber)) {
        this.errorList.mobileNumber = "Please enter valid 10-digit mobile number";
        return false;
      }
      this.errorList.mobileNumber = "";
      return true;
    },

    validateAadhar(aadharNo) {
      if (!aadharNo) {
        this.errorList.aadharNo = "";
        return true; // Optional field
      }
      const pattern = /^\d{4}-\d{4}-\d{4}$/;
      if (!pattern.test(aadharNo)) {
        this.errorList.aadharNo = "Please enter valid Aadhar number (XXXX-XXXX-XXXX)";
        return false;
      }
      this.errorList.aadharNo = "";
      return true;
    },

    validateDL(dlNumber) {
      if (!dlNumber) {
        this.errorList.dlNumber = "Please enter DL number";
        return false;
      }
      // Basic DL validation - adjust pattern as per your requirements
      const pattern = /^[A-Z]{2}[0-9]{2}[0-9]{4}[0-9]{7}$/;
      if (!pattern.test(dlNumber)) {
        this.errorList.dlNumber = "Please enter valid DL number format";
        return false;
      }
      this.errorList.dlNumber = "";
      return true;
    },

    // Modal Methods
    openDriverModal(driver = null) {
      this.resetDriverModal();
      if (driver) {
        this.isEditMode = true;
        this.populateDriverModal(driver);
      } else {
        this.isEditMode = false;
      }
      this.$refs["Driver-modal"].show();
    },

    hideDriverModal() {
      this.$refs["Driver-modal"].hide();
    },

    resetDriverModal() {
      this.driverModal = {
        driverId: "",
        location: "",
        vehicleNumber: "",
        driverType: "",
        eCode: "",
        driverName: "",
        fathersName: "",
        mobileNumber: "",
        dateOfJoining: "",
        dateOfLeaving: "",
        dateOfBirth: "",
        aadharNo: "",
        uanNumber: "",
        esiNo: "",
        dlNumber: "",
      };
      this.errorList = {
        location: "",
        driverType: "",
        driverName: "",
        fathersName: "",
        mobileNumber: "",
        aadharNo: "",
        dlNumber: "",
      };
      this.isEditMode = false;
    },

    populateDriverModal(driver) {
      // Helper function to format date for HTML date input
      const formatDateForInput = (dateString) => {
        if (!dateString || dateString === '' || dateString === 'null') {
          return '';
        }
        
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) {
            return '';
          }
          // Format as YYYY-MM-DD for HTML date input
          return date.toISOString().split('T')[0];
        } catch (error) {
          console.error('Error formatting date:', dateString, error);
          return '';
        }
      };

      this.driverModal = {
        driverId: driver.driverId || "",
        location: driver.driverLocation || "",
        vehicleNumber: driver.driverVehicleNumber || "",
        driverType: driver.driverType === "1" ? "Permanent" : "Temporary",
        eCode: driver.driverECode || "",
        driverName: driver.driverName || "",
        fathersName: driver.driver_fname || "",
        mobileNumber: driver.driverContact || "",
        dateOfJoining: formatDateForInput(driver.driverDateOfJoining),
        dateOfLeaving: formatDateForInput(driver.driverDateOfLeaving),
        dateOfBirth: formatDateForInput(driver.driverDateOfBirth),
        aadharNo: driver.driverAadharNumber || "",
        uanNumber: driver.driverUANNumber || "",
        esiNo: driver.driverEsic || "",
        dlNumber: driver.license_no || "",
      };
      
      console.log('Populated modal data:', this.driverModal);
    },

    validateDriverForm() {
      let isValid = true;

      // Required field validations
      if (!this.driverModal.location) {
        this.errorList.location = "Please enter location";
        isValid = false;
      } else {
        this.errorList.location = "";
      }

      if (!this.driverModal.driverType) {
        this.errorList.driverType = "Please select driver type";
        isValid = false;
      } else {
        this.errorList.driverType = "";
      }

      if (!this.driverModal.driverName) {
        this.errorList.driverName = "Please enter driver name";
        isValid = false;
      } else {
        this.errorList.driverName = "";
      }

      if (!this.driverModal.fathersName) {
        this.errorList.fathersName = "Please enter father's name";
        isValid = false;
      } else {
        this.errorList.fathersName = "";
      }

      if (!this.driverModal.mobileNumber) {
        this.errorList.mobileNumber = "Please enter mobile number";
        isValid = false;
      } else if (!this.validateMobile(this.driverModal.mobileNumber)) {
        isValid = false;
      }

      if (!this.driverModal.dlNumber) {
        this.errorList.dlNumber = "Please enter DL number";
        isValid = false;
      } else if (!this.validateDL(this.driverModal.dlNumber)) {
        isValid = false;
      }

      // Optional field validations
      if (this.driverModal.aadharNo && !this.validateAadhar(this.driverModal.aadharNo)) {
        isValid = false;
      }

      return isValid;
    },

    addDriver() {
      if (!this.validateDriverForm()) {
        this.showError("Please fill all required fields correctly");
        return;
      }

      this.isSubmitting = true;

      const payload = {
        driverLocation: this.driverModal.location,
        driverVehicleNumber: this.driverModal.vehicleNumber,
        driverType: this.driverModal.driverType === "Permanent" ? "1" : "2",
        driverECode: this.driverModal.eCode,
        driverName: this.driverModal.driverName,
        driver_fname: this.driverModal.fathersName,
        driverContact: this.driverModal.mobileNumber,
        driverDateOfJoining: this.driverModal.dateOfJoining,
        driverDateOfLeaving: this.driverModal.dateOfLeaving,
        driverDateOfBirth: this.driverModal.dateOfBirth,
        driverAadharNumber: this.driverModal.aadharNo,
        driverUANNumber: this.driverModal.uanNumber,
        driverEsic: this.driverModal.esiNo,
        license_no: this.driverModal.dlNumber,
      };

      const serviceCall = this.isEditMode
        ? DriverService.updateDriver(this.driverModal.driverId, payload)
        : DriverService.createDriver(payload);

      serviceCall
        .then((response) => {
          if (response.status === 200 && response.data) {
            const message = this.isEditMode ? "Driver updated successfully" : "Driver added successfully";
            this.showSuccess(message);
            this.hideDriverModal();
            this.loadDriverData(); // Reload the data
          } else {
            throw new Error(this.isEditMode ? "Failed to update driver" : "Failed to add driver");
          }
        })
        .catch((error) => {
          const message = this.isEditMode ? "Failed to update driver" : "Failed to add driver";
          this.showError(message);
          console.error("Error saving driver:", error);
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    // Initialization
    initializeComponent() {
      if (!this.checkAuthentication()) {
        return;
      }

      if (!this.checkPermissions()) {
        return;
      }

      this.loadDriverData();
    },

    checkAuthentication() {
      const loginId = this.$sessionsStorage.get("loginId");
      if (!loginId) {
        this.$router.push({ path: "/Login/" });
        return false;
      }
      return true;
    },

    checkPermissions() {
      const permissionStatus = globalPermissionStatus("driverMaster");
      if (permissionStatus === "Disabled") {
        this.$router.push({ path: "/dashboard" });
        return false;
      }

      this.$sessionsStorage.set("PermissionStatus", permissionStatus);
      this.$store.dispatch("setCurrentRoute", "/vehicle/driverMaster");
      return true;
    },

    // Data Loading
    loadDriverData() {
      this.isLoading = true;
      this.fetchDriverData("0", "1", "1", "Normal")
        .then(() => {
          this.isLoading = false;
        })
        .catch((error) => {
          this.showError("Failed to load driver data");
          console.error("Error loading driver data:", error);
          this.isLoading = false;
        });
    },

    fetchDriverData(current, search, pageNumber, type) {
      const params = {
        page: current / this.perPage + 1,
        limit: this.perPage,
        search: this.searchQuery,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc
      };

      return DriverService.getAllDrivers(params)
        .then((response) => {
          if (response.status === 200 && response.data) {
            this.handleDriverDataResponse(response.data, search, pageNumber);
          } else {
            throw new Error("Invalid response from server");
          }
        })
        .catch((error) => {
          console.error("Error fetching driver data:", error);
          // Handle 404 or other API errors gracefully
          if (error.response && error.response.status === 404) {
            this.driverList = [];
            this.totalRows = 0;
            this.showError("Driver API endpoint not found. Please check backend configuration.");
          } else {
            this.driverList = [];
            this.totalRows = 0;
            this.showError("Failed to load driver data. Please try again later.");
          }
          throw error;
        });
    },

        handleDriverDataResponse(data, search, pageNumber) {
      if (search !== "2") {
        this.currentPage = 1;
        this.totalRows = data.meta.total || 0;
      } else {
        this.currentPage = pageNumber;
      }

      if (data.data && data.data.length > 0) {
        this.driverList = data.data;
        this.totalRows = data.meta.total || data.data.length;
      } else {
        this.driverList = [];
        this.totalRows = 0;
      }
    },

    // Search and Pagination
    handleSearch() {
      this.debounceSearch();
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadDriverData();
      }, 300);
    },

    clearSearch() {
      this.searchQuery = "";
      this.loadDriverData();
    },

    handlePageChange(event) {
      const current = event === 1 ? 0 : (event * this.perPage) - this.perPage;
      this.storedCurrentPage = current;

      this.isLoading = true;
      this.fetchDriverData(current, "2", event, "Normal")
        .then(() => {
          this.isLoading = false;
        })
        .catch((error) => {
          this.showError("Failed to load page data");
          console.error("Error loading page data:", error);
          this.isLoading = false;
        });
    },

    // Navigation
    navigateToAddDriver() {
      this.$router.push({ path: "/vehicle/driverMaster" });
    },

    viewDriverDetails(driverId) {
      this.$sessionsStorage.set("editDriverId", driverId);
      this.$router.push({ path: "/vehicle/driverMaster" });
    },

    viewDriverProfile(driverId) {
      this.$router.push({ path: `/vehicle/driverProfile/${driverId}` });
    },

    // Delete Operations
    confirmDeleteDriver(driver) {
      this.driverToDelete = driver;
      this.showDeleteModal = true;
    },

    cancelDelete() {
      this.driverToDelete = null;
      this.showDeleteModal = false;
    },

    deleteDriver() {
      if (!this.driverToDelete) return;

      this.isLoading = true;

      DriverService.deleteDriver(this.driverToDelete.driverId)
        .then((response) => {
          if (response.status === 200 && response.data) {
            this.showSuccess(`Driver "${this.driverToDelete.driverName}" deleted successfully`);
            this.loadDriverData();
          } else {
            throw new Error("Failed to delete driver");
          }
        })
        .catch((error) => {
          this.showError("Failed to delete driver");
          console.error("Error deleting driver:", error);
        })
        .finally(() => {
          this.isLoading = false;
          this.cancelDelete();
        });
    },

    // Import Operations
    importExcel() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls';
      input.onchange = (event) => {
        const file = event.target.files[0];
        if (file) {
          this.processExcelFile(file);
        }
      };
      input.click();
    },

    processExcelFile(file) {
      this.isImporting = true;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = read(data, { 
            type: 'array',
            cellDates: true,
            cellNF: false,
            cellText: false
          });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = utils.sheet_to_json(worksheet, { 
            header: 1,
            raw: false,
            dateNF: 'yyyy-mm-dd'
          });

          this.processImportedData(jsonData);
        } catch (error) {
          this.showError("Failed to process Excel file");
          console.error("Error processing Excel file:", error);
        } finally {
          this.isImporting = false;
        }
      };
      reader.readAsArrayBuffer(file);
    },

    processImportedData(jsonData) {
      if (!jsonData || jsonData.length < 2) {
        this.showError("Invalid Excel file format");
        return;
      }

      const headers = jsonData[0];
      const dataRows = jsonData.slice(1);
      
      console.log("Headers:", headers);
      console.log("Sample data row:", dataRows[0]);

      // Map Excel columns to our data structure
      const mappedData = dataRows.map((row, index) => {
        // Helper function to convert Excel date to proper format
        const convertExcelDate = (excelDate) => {
          console.log("Converting date:", excelDate, "Type:", typeof excelDate);
          
          if (!excelDate || excelDate === '' || excelDate === '  -   -') {
            return '';
          }
          
          // If it's already a string date, return as is
          if (typeof excelDate === 'string' && excelDate.includes('-')) {
            return excelDate;
          }
          
          // If it's a number (Excel serial date), convert it
          if (typeof excelDate === 'number') {
            const date = new Date((excelDate - 25569) * 86400 * 1000);
            const result = date.toISOString().split('T')[0];
            console.log("Converted date:", result);
            return result;
          }
          
          return '';
        };

        const driverData = {
          driverLocation: row[0] || '',
          driverVehicleNumber: row[1] || '',
          driverType: row[2] === 'Permanent' ? '1' : '2',
          driverECode: row[3] || '',
          driverName: row[4] || '',
          driver_fname: row[5] || '',
          driverContact: row[6] || '',
          driverDateOfJoining: convertExcelDate(row[7]),
          driverDateOfLeaving: convertExcelDate(row[8]),
          driverDateOfBirth: convertExcelDate(row[9]),
          driverAadharNumber: row[10] || '',
          driverUANNumber: row[11] || '',
          driverEsic: row[12] || '',
          license_no: row[13] || ''
        };

        return driverData;
      });

      this.uploadImportedData(mappedData);
    },

    uploadImportedData(driverData) {
      console.log("Uploading imported driver data:", driverData);
      if (!Array.isArray(driverData) || driverData.length === 0) {
        this.showError("No valid driver data to import");
        return;
      }

      const payload = {
        excel_data: driverData,
        action: 'import'
      };

      DriverService.importDrivers(payload)
        .then((response) => {
          if (response.status === 200 && response.data) {
            this.showSuccess(`Successfully imported ${driverData.length} drivers`);
            this.loadDriverData(); // Reload the data
          } else {
            throw new Error("Failed to import data");
          }
        })
        .catch((error) => {
          this.showError("Failed to import driver data");
          console.error("Error importing data:", error);
        })
        .finally(() => {
          this.isImporting = false;
        });
    },

    // Export Operations
    exportDriverReport() {
      if (!this.hasDriverData) {
        this.showError("No data to export");
        return;
      }

      this.isExporting = true;

      const params = {
        search: this.searchQuery,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        page: this.currentPage,
        limit: this.perPage
      };

      DriverService.exportDrivers(params)
        .then((response) => {
          if (response.status === 200 && response.data) {
            // Create a blob from the response data
            const blob = new Blob([response.data], { 
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Driver_Master_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            this.showSuccess("Report exported successfully");
          } else {
            throw new Error("Failed to export data");
          }
        })
        .catch((error) => {
          this.showError("Failed to export report");
          console.error("Error exporting report:", error);
        })
        .finally(() => {
          this.isExporting = false;
        });
    },



    // Utility Methods
    formatDate(dateString) {
      if (!dateString || dateString === '' || dateString === 'null') {
        return 'N/A';
      }
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return 'N/A';
        }
        return date.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return 'N/A';
      }
    },

    showSuccess(message) {
      this.successMessage = message;
      this.showSuccessToast = true;
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.showSuccessToast = false;
      }, 3000);
    },

    showError(message) {
      this.errorMessage = message;
      this.showErrorToast = true;
      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.showErrorToast = false;
      }, 5000);
    },
  },
};
</script>

<style scoped>
.driver-details-container {
  min-height: 100vh;
}

.tableMain {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.formCard {
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Gap utility removed for Vue 2 compatibility */

.font-monospace {
  font-family: 'Courier New', monospace;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }

  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
}

/* Accessibility improvements */
.btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Loading states */
.table-busy {
  opacity: 0.6;
  pointer-events: none;
}

/* Hover effects */
.btn:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

/* Custom scrollbar */
.table-responsive::-webkit-scrollbar {
  height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>



// WEBPACK FOOTER //
// viewDriverDetail.vue?cb5310e8